import BaseEntityNew from 'client/components/core/BaseEntity/BaseEntityNew'
import { EntityProps } from 'client/types'
import CommonButton from 'common/components/entities/Button/button'
import { EntityTypeEnum } from 'common/enums/entityTypeEnum'
import { ButtonInterface } from 'common/types/entities/button-interface'
import { ContactUsButtonInterface } from 'common/types/entities/contact-us-button-interface'
import { MasterBlockId } from 'src/shared/types/base-entity-interface'
import { BorderStyleEnum } from 'common/types/styleTypes'
import React from 'react'
import { useCalculateButtonStyles } from 'src/editor/shared/hooks/use-calculate-button-styles'
import { useSelectedButtonColor } from 'src/editor/shared/hooks/use-theme-color/use-selected-button-color'
import { generateBaseEntity } from 'src/shared/entities/utils/generate-base-entity'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { useContactUsDescendantIds } from './ContactUs'

function ContactUsButton({
  entity,
  isMoveDownAllowed,
  isMoveUpAllowed,
}: EntityProps<ContactUsButtonInterface>) {
  const entityIds = useContactUsDescendantIds()

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    // calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    buttonType: entity.buttonType,
    fontSize: entity.textFontSize,
    mobileFontSize: entity.mobileTextFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    fontFamily: entity.textFontFamily,
    mobileFontFamily: entity.mobileTextFontFamily,
    fontWeight: entity.textFontWeight,
    mobileFontWeight: entity.mobileTextFontWeight,
    fontStyle: entity.textFontStyle,
    mobileFontStyle: entity.mobileTextFontStyle,
  })

  const {
    buttonTextColor,
    buttonBackgroundColor,
    hoveredButtonBackgroundColor,
  } = useSelectedButtonColor(
    entity.buttonType || WebsiteSettingsTypographyTypeEnum.PrimaryButton,
  )

  return (
    <BaseEntityNew
      entity={entity}
      isMoveDownAllowed={isMoveDownAllowed}
      isMoveUpAllowed={isMoveUpAllowed}
      isRemoveAllowed={false}
      isSaveAllowed={false}
      isCopyAllowed={false}
      dropBoxAllowedEntityIds={entityIds}
      flex
      flexColumn
    >
      <CommonButton
        iconClassNameBefore={entity.iconClassNameBefore}
        iconClassNameAfter={entity.iconClassNameAfter}
        text={entity.text}
        border={calculatedBorder.desktop}
        mobileBorder={calculatedBorder.mobile}
        subText={entity.subText}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        alignSelf={entity.alignSelf}
        background={
          (entity.background ?? entity.backgroundColor) || buttonBackgroundColor
        }
        mobileBackground={
          (entity.mobileBackground ?? entity.mobileBackgroundColor) ||
          buttonBackgroundColor
        }
        attrId={entity.htmlAttrId}
        width={entity.width}
        mobileWidth={entity.mobileWidth}
        textColor={entity.textColor || buttonTextColor}
        mobileTextColor={entity.mobileTextColor || buttonTextColor}
        subTextColor={entity.subTextColor}
        mobileSubTextColor={entity.mobileSubTextColor}
        textFontSize={calculatedFontSize.desktop}
        mobileTextFontSize={calculatedFontSize.mobile}
        subTextFontSize={entity.subTextFontSize}
        mobileSubTextFontSize={entity.mobileSubTextFontSize}
        textFontFamily={calculatedFontFamily.desktop}
        textFontWeight={calculatedFontWeight.desktop}
        textFontStyle={calculatedFontStyle.desktop}
        lineHeight={calculatedLineHeight.desktop}
        subTextFontFamily={entity.subTextFontFamily}
        subTextFontWeight={entity.subTextFontWeight}
        subTextFontStyle={entity.subTextFontStyle}
        mobileTextFontFamily={calculatedFontFamily.mobile}
        mobileTextFontWeight={calculatedFontWeight.mobile}
        mobileTextFontStyle={calculatedFontStyle.mobile}
        mobileLineHeight={calculatedLineHeight.mobile}
        mobileSubTextFontFamily={entity.mobileSubTextFontFamily}
        mobileSubTextFontWeight={entity.mobileSubTextFontWeight}
        mobileSubTextFontStyle={entity.mobileSubTextFontStyle}
        boxShadow={entity.boxShadow}
        mobileBoxShadow={entity.mobileBoxShadow}
        hover={{
          ...entity.hover,
          backgroundColor:
            entity?.hover?.backgroundColor || hoveredButtonBackgroundColor,
        }}
        mobileHover={{
          ...entity.mobileHover,
          backgroundColor:
            entity?.mobileHover?.backgroundColor ||
            entity?.hover?.backgroundColor ||
            hoveredButtonBackgroundColor,
        }}
      />
    </BaseEntityNew>
  )
}

export function createContactUsButton(
  parentId: string,
  text: string,
  masterBlockId?: MasterBlockId,
): ContactUsButtonInterface {
  return {
    ...generateBaseEntity(
      EntityTypeEnum.ContactUsButton,
      parentId,
      masterBlockId,
    ),
    textFontSize: 20,
    subTextFontSize: 14,
    textColor: 'rgba(255, 255, 255, 1)',
    background: 'rgba(1, 160, 255, 1)',
    alignSelf: 'center',
    text,
    border: {
      radius: 5,
      style: BorderStyleEnum.none,
    },
    padding: {
      paddingTop: 15,
      paddingRight: 90,
      paddingBottom: 15,
      paddingLeft: 90,
    },
  }
}

export function getButtonFontProperties(
  entity: Pick<
    ButtonInterface,
    | 'textFontFamily'
    | 'textFontStyle'
    | 'textFontWeight'
    | 'mobileTextFontFamily'
    | 'mobileTextFontStyle'
    | 'mobileTextFontWeight'
    | 'subTextFontFamily'
    | 'subTextFontStyle'
    | 'subTextFontWeight'
    | 'mobileSubTextFontFamily'
    | 'mobileSubTextFontStyle'
    | 'mobileSubTextFontWeight'
  >,
) {
  const fontProperties = []

  if (entity.textFontFamily) {
    fontProperties.push({
      fontFamily: entity.textFontFamily,
      fontStyle: entity.textFontStyle,
      fontWeight: entity.textFontWeight,
    })
  }

  if (entity.mobileTextFontFamily) {
    fontProperties.push({
      fontFamily: entity.mobileTextFontFamily,
      fontStyle: entity.mobileTextFontStyle,
      fontWeight: entity.mobileTextFontWeight,
    })
  }

  if (entity.subTextFontFamily) {
    fontProperties.push({
      fontFamily: entity.subTextFontFamily,
      fontStyle: entity.subTextFontStyle,
      fontWeight: entity.subTextFontWeight,
    })
  }

  if (entity.mobileSubTextFontFamily) {
    fontProperties.push({
      fontFamily: entity.mobileSubTextFontFamily,
      fontStyle: entity.mobileSubTextFontStyle,
      fontWeight: entity.mobileSubTextFontWeight,
    })
  }

  return fontProperties
}

export function getNewButtonCustomFontProperties(
  entity: Pick<
    ButtonInterface,
    | 'textFontFamily'
    | 'textFontFileId'
    | 'mobileTextFontFamily'
    | 'mobileTextFontFileId'
    | 'subTextFontFamily'
    | 'subTextFontFileId'
    | 'mobileSubTextFontFamily'
    | 'mobileSubTextFontFileId'
  >,
) {
  const fontProperties = []

  if (entity.textFontFamily && entity.textFontFileId) {
    fontProperties.push({
      fontFamily: entity.textFontFamily,
      fontFileId: entity.textFontFileId,
    })
  }

  if (entity.mobileTextFontFamily && entity.mobileTextFontFileId) {
    fontProperties.push({
      fontFamily: entity.mobileTextFontFamily,
      fontFileId: entity.mobileTextFontFileId,
    })
  }

  if (entity.subTextFontFamily && entity.subTextFontFileId) {
    fontProperties.push({
      fontFamily: entity.subTextFontFamily,
      fontFileId: entity.subTextFontFileId,
    })
  }

  if (entity.mobileSubTextFontFamily && entity.mobileSubTextFontFileId) {
    fontProperties.push({
      fontFamily: entity.mobileSubTextFontFamily,
      fontFileId: entity.mobileSubTextFontFileId,
    })
  }

  return fontProperties
}

export default ContactUsButton
